/**
 * Invoice numbering series, in the invoice detail's visual language:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-list/table.tsx (inline edit)
 *   one entry per series with its format edited in place;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks for each series' figures;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid — the series on the left, opening a new one beside them.
 *
 * An edit cannot renumber anything — the format applies only to numbers issued from here on. Gaps
 * are shown, never hidden, and never reused. Delete… is offered only while the API's `nextNumber` is 1
 * (nothing issued yet); the server refuses it otherwise, and confirms through `useConfirm`.
 */
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  PiHashDuotone as NextIcon,
  PiListNumbersDuotone as SeriesIcon,
  PiPlusCircleDuotone as AddIcon,
  PiTextAaDuotone as FormatIcon,
  PiTrashDuotone as DeleteIcon,
  PiWarningDuotone as GapIcon,
} from "react-icons/pi";
import { Badge, Button, Input, Text } from "rizzui";
import { useLoaderData, useOutletContext, useRevalidator } from "react-router-dom";
import { CaveatBanner, EmptyState, FieldError, WidgetCard } from "@steelyard/theme";
import { CardTitle, InfoBlock, PageHero } from "../../components/detail-parts";
import { useConfirm } from "../../components/confirm-modal";
import { can } from "../../lib/permissions";
import { changeSeriesFormat, createInvoiceSeries, deleteInvoiceSeries, listInvoiceSeries, type InvoiceSeries } from "../../lib/services/invoicing";
import type { AccountSummary, PermissionState } from "../../types";

const ABSENT_GSTIN = "Which GSTIN each series belongs to is not shown here. Active and inactive series are both listed.";

export async function invoiceSeriesLoader() {
  return { series: await listInvoiceSeries() };
}

export default function InvoiceSeriesPage() {
  const { series } = useLoaderData() as Awaited<ReturnType<typeof invoiceSeriesLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const revalidator = useRevalidator();
  const mayWrite = can(permissionState, "billing.administer");
  const active = series.filter((s) => s.isActive).length;

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Invoicing", href: "/invoicing" }, { name: "Invoice Series" }]}
        back={{ href: "/invoicing", label: "All invoices" }}
        icon={<SeriesIcon aria-hidden />}
        title="Invoice Series"
        description="The financial-year-aware numbering an invoice is issued from."
        meta={
          <>
            <Badge variant="flat" color="secondary" rounded="pill">
              {series.length} series
            </Badge>
            <Badge variant="flat" color="success" rounded="pill">
              {active} active
            </Badge>
          </>
        }
      />

      <CaveatBanner caveat={ABSENT_GSTIN}>
        <div className="flex flex-col gap-6 @5xl:grid @5xl:grid-cols-12 @5xl:items-start">
          <div className="flex flex-col gap-6 @5xl:col-span-8">
            {series.length === 0 ? (
              <WidgetCard rounded="xl">
                <EmptyState title="No series configured">
                  No numbering series exists yet. The first invoice raised opens one automatically from the configured
                  format{mayWrite ? ", or you can open one now" : ""}.
                </EmptyState>
              </WidgetCard>
            ) : (
              series.map((s) => <SeriesCard key={s.id} series={s} mayWrite={mayWrite} onSaved={() => revalidator.revalidate()} />)
            )}
          </div>
          {mayWrite ? (
            <div className="@5xl:col-span-4">
              <SeriesCreateForm onSaved={() => revalidator.revalidate()} />
            </div>
          ) : null}
        </div>
      </CaveatBanner>
    </div>
  );
}

function SeriesCard({ series: s, mayWrite, onSaved }: { series: InvoiceSeries; mayWrite: boolean; onSaved: () => void }) {
  const gapped = s.gaps.length > 0;
  const confirm = useConfirm();
  const remove = () =>
    confirm({
      title: `Delete series ${s.seriesCode} (FY ${s.financialYear})`,
      consequence: "Only a series that has never issued a number can be deleted. This cannot be undone.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await deleteInvoiceSeries(s.id);
          toast.success("Series deleted");
          onSaved();
        } catch (err) {
          toast.error((err as { detail?: string; title?: string }).detail ?? (err as { title?: string }).title ?? "The series was not deleted.");
        }
      },
    });
  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<SeriesIcon aria-hidden className="size-5" />} tile={s.isActive ? "bg-green-lighter text-green-dark" : "bg-gray-100 text-gray-600"}>
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{s.seriesCode}</span>
            <Badge variant="outline" rounded="pill" size="sm" className="border-muted font-normal text-gray-600">
              FY {s.financialYear}
            </Badge>
          </span>
        </CardTitle>
      }
      action={
        <span className="flex items-center gap-2">
          {s.isActive ? (
            <Badge variant="flat" color="success" rounded="pill">
              Active
            </Badge>
          ) : (
            <Badge variant="flat" rounded="pill" className="bg-gray-100 text-gray-600">
              Inactive
            </Badge>
          )}
          {mayWrite && Number(s.nextNumber) === 1 ? (
            <Button size="sm" variant="outline" color="danger" onClick={remove}>
              <DeleteIcon aria-hidden className="me-1.5 size-4" />
              Delete
            </Button>
          ) : null}
        </span>
      }
      headerClassName="items-center"
    >
      <div className="mt-5 grid gap-5 @2xl:grid-cols-3">
        <InfoBlock icon={<FormatIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Format">
          <Text className="break-all font-mono text-sm text-gray-900">{s.format}</Text>
        </InfoBlock>
        <InfoBlock icon={<NextIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Next number">
          <Text className="font-lexend text-lg font-semibold tabular-nums text-gray-900">{String(s.nextNumber)}</Text>
        </InfoBlock>
        <InfoBlock icon={<GapIcon aria-hidden className="size-4" />} tile={gapped ? "bg-red-lighter text-red-dark" : "bg-green-lighter text-green-dark"} label="Gaps">
          {gapped ? <Text className="font-mono text-sm text-red-dark">{s.gaps.map(String).join(", ")}</Text> : <Text className="text-sm text-gray-500">none</Text>}
        </InfoBlock>
      </div>

      {gapped ? (
        <div className="mt-5">
          <CaveatBanner
            caveat={
              <>
                These sequences were issued but carry no invoice. They are listed rather than hidden and are never reused — reissuing one
                would put two documents in the same numbering position.
              </>
            }
          />
        </div>
      ) : null}

      {mayWrite ? <SeriesFormatForm series={s} onSaved={onSaved} /> : null}
    </WidgetCard>
  );
}

// The server's own token rule (`InvoiceSeries.SequenceToken`): {SEQ}, or {SEQ:000000} padded with zeros only.
const SEQ_TOKEN = /\{SEQ(?::0+)?\}/;
const SEQ = "Include {SEQ} for the running number — or {SEQ:000000} to pad it with zeros (zeros only, e.g. not 000001).";
const formatSchema = z.object({ format: z.string().trim().min(1, "A format is required.").refine((f) => SEQ_TOKEN.test(f), SEQ) });

/** "2026-27": the second year is the first plus one. Blank means the current financial year. */
const isYearLabel = (y: string) => y === "" || (/^\d{4}-\d{2}$/.test(y) && (Number(y.slice(0, 4)) + 1) % 100 === Number(y.slice(5)));

const createSchema = z.object({
  seriesCode: z.string().trim().min(1, "A series code is required."),
  financialYear: z.string().trim().refine(isYearLabel, "Use two consecutive years, e.g. 2026-27."),
  format: z.string().trim().refine((f) => f === "" || SEQ_TOKEN.test(f), SEQ),
});

/**
 * The pattern for what was typed: `TEST-2027-{SEQ:000000}` — the owner's INV-2026-000001 shape (2026-10-15),
 * the year being the one the financial year starts in. A blank code or year stays a token, so it still
 * resolves server-side.
 */
const patternFor = (code: string, year: string) => `${code.trim() || "{CODE}"}-${year.trim().slice(0, 4) || "{YYYY}"}-{SEQ:000000}`;

/** What each token in a format becomes — shown under both format inputs. */
function FormatHelp() {
  return (
    <span className="block">
      Type the pattern, not a number: <code>{"{CODE}"}</code> series code · <code>{"{YYYY}"}</code> year the financial
      year starts (2026) · <code>{"{FY}"}</code> financial year (2026-27) ·{" "}
      <code>{"{SEQ}"}</code> running number (<code>{"{SEQ:000000}"}</code> pads it to six digits).
    </span>
  );
}

/**
 * The numbers a pattern would print, as the person types. Display only — the number an invoice gets is
 * still the server's own rendering (`InvoiceSeries.Render`), never this.
 */
function FormatPreview({ format, code, year, from }: { format: string; code: string; year: string; from: number }) {
  const f = format.trim();
  if (!f) return null;
  if (!SEQ_TOKEN.test(f)) return <span className="block font-medium text-red">Every invoice would print exactly “{f}” — put {"{SEQ}"} where the running number goes.</span>;
  const at = (n: number) =>
    f
      .replace(/\{SEQ(?::(0+))?\}/g, (_, pad?: string) => String(n).padStart(pad?.length ?? 0, "0"))
      .replaceAll("{CODE}", code.trim() || "CODE")
      .replaceAll("{FY}", year.trim() || "this-year")
      .replaceAll("{YYYY}", year.trim().slice(0, 4) || "this-year");
  return (
    <span className="block font-medium text-gray-700">
      Numbers will look like <code>{at(from)}</code>, <code>{at(from + 1)}</code>, …
    </span>
  );
}

function SeriesCreateForm({ onSaved }: { onSaved: () => void }) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof createSchema>>({ resolver: zodResolver(createSchema), defaultValues: { seriesCode: "", financialYear: "", format: "" } });
  const [code, year, format] = watch(["seriesCode", "financialYear", "format"]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await createInvoiceSeries({
        seriesCode: values.seriesCode,
        financialYear: values.financialYear || undefined,
        format: values.format || undefined,
      });
      toast.success("Series created");
      reset();
      onSaved();
    } catch (err) {
      toast.error((err as { detail?: string; title?: string }).detail ?? (err as { title?: string }).title ?? "The series was refused.");
    }
  });

  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<AddIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
          Open a series
        </CardTitle>
      }
      headerClassName="items-center"
    >
      <form onSubmit={onSubmit} noValidate className="mt-5 flex flex-col gap-4">
        <Input label="Series code" placeholder="INV" inputClassName="font-mono" error={errors.seriesCode?.message} {...register("seriesCode")} />
        <Input
          label="Financial year"
          placeholder="e.g. 2026-27 — blank for the current year"
          inputClassName="font-mono"
          error={errors.financialYear?.message}
          {...register("financialYear")}
        />
        <Input
          label="Number pattern"
          placeholder="Blank for the default pattern"
          inputClassName="font-mono"
          error={errors.format?.message}
          {...register("format")}
        />
        <div className="text-xs text-gray-500">
          <FormatPreview format={format} code={code} year={year} from={1} />
          <FormatHelp />
          <Button
            type="button"
            variant="text"
            size="sm"
            className="mt-1 h-auto p-0 text-sm font-medium text-blue-dark underline hover:text-blue"
            onClick={() => setValue("format", patternFor(code, year), { shouldValidate: true })}
          >
            Generate from series code and year
          </Button>
        </div>
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? "Creating…" : "Create series"}
        </Button>
      </form>
    </WidgetCard>
  );
}

function SeriesFormatForm({ series, onSaved }: { series: InvoiceSeries; onSaved: () => void }) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof formatSchema>>({ resolver: zodResolver(formatSchema), defaultValues: { format: series.format } });
  const format = watch("format");

  const onSubmit = handleSubmit(async (values) => {
    try {
      await changeSeriesFormat(series.id, values.format);
      toast.success("Series format saved");
      onSaved();
    } catch (err) {
      toast.error((err as { detail?: string; title?: string }).detail ?? (err as { title?: string }).title ?? "The format was refused.");
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="mt-5 flex flex-col gap-2 rounded-xl border border-dashed border-muted bg-gray-50 p-4 dark:bg-gray-100/40">
      <label htmlFor={`format-${series.id}`} className="text-sm font-semibold text-gray-900">
        Change the format
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={`format-${series.id}`}
          aria-describedby={`format-${series.id}-hint`}
          className="min-w-56 flex-1"
          inputClassName="font-mono"
          {...register("format")}
        />
        <Button type="submit" variant="outline" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Save format"}
        </Button>
      </div>
      <p id={`format-${series.id}-hint`} className="text-xs text-gray-500">
        <FormatPreview format={format} code={series.seriesCode} year={series.financialYear} from={Number(series.nextNumber)} />
        <FormatHelp />
        Applies to numbers issued from now on. It does not renumber any invoice already raised, does not change the next
        number, and does not close a gap — an invoice number is already on paper and in Tally by the time it is here.
      </p>
      {errors.format ? <FieldError message={errors.format.message} /> : null}
    </form>
  );
}
